import cn from 'clsx'
import React, { useCallback, useContext, useRef, useEffect } from 'react'
import BedbankRoomOptions from './BedbankRoomOptions'
import BedbankSidebarFilters from './BedbankSidebarFilters'
import Pane from 'components/Common/Pane'
import BedbankOfferIntro from './BedbankOfferIntro'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { CALENDAR_GA_EVENT_ACTION, GA_ACTION_TO_SNOWPLOW_MAP } from 'constants/analytics'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import BedbankOfferHeader from './BedbankOfferHeader'
import Backdrop from 'components/utils/Backdrop'
import OfferPageGallery from '../OfferPageGallery'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import CalendarDatePickerSelectBox from './BedbankDatePicker/CalendarDatePickerSelectBox'
import CalendarDatePickerSelect from './BedbankDatePicker/CalendarDatePickerSelect'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import { rem } from 'polished'
import config from 'constants/config'
import Heading from 'LuxKit/Typography/Heading'
import { InViewHookResponse } from 'react-intersection-observer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Subtitle from 'LuxKit/Typography/Subtitle'
import BedbankDurationTabs from './BedbankDurationTabs'
import AnalyticsComponentContext, { AnalyticsComponent } from 'contexts/Analytics/analyticsComponentContext'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BedbankOfferPageStickyFooter from '../BedbankOfferPageStickyFooter'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import BedbankOfferFlightDealBanner from './BedbankOfferFlightDealBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { isFlashCalendarEnabled, isAnyCalendarExist } from 'selectors/offerPage/bedbankOfferPageSelectors'
import useShareModalConfig from 'hooks/useShareModalConfig'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import BedbankRoomOptionsMobile from './MobileBookingFlow/BedbankRoomOptionsMobile'
import BedbankOfferPageStickyFooterMobile from './MobileBookingFlow/BedbankOfferPageStickyFooterMobile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankHotelFlightsToggleButton from '../Accommodation/HotelFlightsToggle/BedbankHotelFlightsToggleButton'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import BedbankMobileStickyHeaderFilters from './MobileBookingFlow/BedbankMobileStickyHeaderFilters'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { BusinessTravellerMultiRoomHotelsOfferPageDesktop, BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'

const RelativeBlock = styled.div`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} 0;
  }
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const FlexGroup = styled(Group)`
  flex-grow: 1;
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;
`

interface Props {
  offer: App.BedbankOffer;
  checkInDate?: string;
  checkOutDate?: string;
  rooms?: Array<App.Occupants>;
  activeFilter?: OfferPageFilterType;
  flightsEnabled: boolean;
  isCalendarEnabled: boolean;
  isNightBarEnabled: boolean;
  duration: number;
  canShowYmalFooter: boolean;
  ymalStickyFooterHideTriggerRef: InViewHookResponse['ref'],
  ymalStickyFooterShowTriggerRef: InViewHookResponse['ref'],
  airport?: App.Airport;
  isFlightsSelected?: boolean;
}

function BedbankOfferContent({
  activeFilter,
  offer,
  rooms = initialRooms,
  checkInDate,
  checkOutDate,
  flightsEnabled,
  duration,
  isCalendarEnabled,
  isNightBarEnabled,
  canShowYmalFooter,
  ymalStickyFooterHideTriggerRef,
  ymalStickyFooterShowTriggerRef,
  airport,
  isFlightsSelected,
}: Props) {
  const dispatch = useOfferPageDispatch()

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: airport?.code,
  })

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToOptions = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      scrollToPackageOptions()
    }
  }, [isTabletScreenSizeAndUp, scrollToPackageOptions])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
    trackEvent('VIEW_PACKAGE_OPTIONS_PRESSED')
  }, [scrollToPackageOptions, trackEvent])

  useEffect(() => {
    if (activeFilter) {
      autoScrollToOptions()
    }
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const onUserAnalyticsAction = useCallback((action: string) => {
    const snowplowEvent = GA_ACTION_TO_SNOWPLOW_MAP[action]
    if (snowplowEvent) {
      trackEvent(snowplowEvent)
    }
  }, [trackEvent])

  const closeFilters = useCallback(() => {
    if (activeFilter === 'checkin') {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseFilter)
    }
    dispatch({ type: OfferPageActions.closeFilter })
  }, [onUserAnalyticsAction, activeFilter, dispatch])
  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')
  const shareModalConfig = useShareModalConfig({
    type: 'offer',
    image: offer.images[0],
    offerId: offer.id,
    utmCampaign: 'webshare',
    location,
    offerType: 'bedbank_hotel',
  })

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const showOfferBanner = offer.flights.airportCode && airport?.code && !fetchingRates && !config.businessTraveller.isEnabled

  const showHotelAndFlightsToggle = isTabletScreenSizeAndUp && flightsEnabled

  return (
    <UserAnalyticsActionContext.Provider value={onUserAnalyticsAction}>
      <AccommodationOfferPageStickyHeaderMobile>
        <BedbankMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>
      <RelativeBlock>
        <CSSBreakpoint min="tablet">
          <BedbankOfferHeader
            offer={offer}
            rooms={rooms}
            duration={duration}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            onViewPackageOptions={onViewPackageOptions}
            flightPrice={flightPrice}
            flightsEnabled={flightsEnabled}
          >
          </BedbankOfferHeader>
        </CSSBreakpoint>
        <span ref={ymalStickyFooterHideTriggerRef} />
        <OfferPageGallery offer={offer} shareModalConfig={shareModalConfig} />
        <Breakpoint max="mobile">
          <BedbankSidebarFilters offer={offer} />
        </Breakpoint>
        <Pane>
          <BedbankOfferIntro
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            offer={offer}
            duration={duration}
            onViewPackageOptions={onViewPackageOptions}
            rooms={rooms}
            flightPrice={flightPrice}
            flightsEnabled={flightsEnabled}
            shareModalConfig={shareModalConfig}
          />
        </Pane>
      </RelativeBlock>
      <span ref={ymalStickyFooterShowTriggerRef} />
      <PackageOptionsPane ref={packageOptionsAnchor} id="Package-Options">
        <SoftOfferPageWalledGarden>
          <CSSBreakpoint max="mobile">
            <BedbankOfferPageStickyFooterMobile />
            <VerticalSpacer gap={16}>
              <LayoutContainer>
                <VerticalSpacer gap={16}>
                  <Heading variant="heading4" format="titlecase">
                    {config.brandConfig.travelSynonym} Options
                  </Heading>
                  <BedbankHotelFlightsToggleButton />
                </VerticalSpacer>
              </LayoutContainer>
              <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
                <BedbankRoomOptionsMobile
                  packageViews={packageViews}
                  fetchingRates={fetchingRates}
                  offer={offer}
                  onViewPackageOptions={onViewPackageOptions}
                />
              </AnalyticsComponentContext.Provider>
            </VerticalSpacer>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <VerticalSpacer gap={20}>
              <LayoutContainer>
                <VerticalSpacer gap={32}>
                  <VerticalSpacer gap={16}>
                    <Heading variant="heading3" format="titlecase" as="h2" align="center">
                      {config.brandConfig.travelSynonym} Options
                    </Heading>
                    <CSSBreakpoint max="mobile">
                      <Group direction="vertical" gap={8}>
                        <OfferCapacityWithAgesSelectBox />
                        {isCalendarEnabled ? <CalendarDatePickerSelectBox /> : <OfferDatePickerSelectBox />}
                        {flightsEnabled && <OfferAirportSelectBox />}
                      </Group>
                    </CSSBreakpoint>
                    <CSSBreakpoint min="tablet">
                      {showHotelAndFlightsToggle && (
                        <Group ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="space-between" wrap="wrap">
                          <FlexGroup direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start">
                            <BedbankHotelFlightsToggleButton />
                          </FlexGroup>
                          <FlexDesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="end" className={cn({ open: !!activeFilter })}>
                            {flightsEnabled && isFlightsSelected && <OfferAirportSelect />}
                            {/* Put the offer page search component to here */}
                            <BusinessTravellerAccountGuard
                              employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
                              loading={<BusinessTravellerSelectLoadingSkeleton />}
                              fallback={<BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled />}
                            >
                              <BusinessTravellerMultiRoomHotelsOfferPageDesktop />
                            </BusinessTravellerAccountGuard>
                            {isCalendarEnabled ? <CalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} /> : <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                            <Backdrop onClick={closeFilters} visible={!!activeFilter} data-testid="filter-backdrop" />
                          </FlexDesktopFilterBlock>
                        </Group>
                      )}
                      {!showHotelAndFlightsToggle && (
                        <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                          {/* put the offer page search component to here */}
                          <BusinessTravellerAccountGuard
                            employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
                            loading={<BusinessTravellerSelectLoadingSkeleton />}
                            fallback={<BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled />}
                          >
                            <BusinessTravellerMultiRoomHotelsOfferPageDesktop />
                          </BusinessTravellerAccountGuard>
                          {isCalendarEnabled ? <CalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} /> : <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                          {flightsEnabled && <OfferAirportSelect />}
                          <Backdrop onClick={closeFilters} visible={!!activeFilter} data-testid="filter-backdrop" />
                        </DesktopFilterBlock>
                      )}
                    </CSSBreakpoint>
                  </VerticalSpacer>
                  {isNightBarEnabled && (<VerticalSpacer gap={8}>
                    <Subtitle variant="subtitle3" align="center">
                      Select duration
                    </Subtitle>
                    <BedbankDurationTabs />
                  </VerticalSpacer>)}
                </VerticalSpacer>
              </LayoutContainer>
              <VerticalSpacer gap={16}>
                <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
                  <LayoutContainer>
                    <VerticalSpacer gap={16}>
                      {!isCalendarEnabled &&
                        <BodyText variant="large" align="center" colour="neutral-one">
                          Select your {config.brandConfig.travelSynonym} options
                        </BodyText>}

                      {showOfferBanner && config.ENABLE_BUNDLED_FLIGHTS && <BedbankOfferFlightDealBanner
                        destinationAirportCode={offer.flights.airportCode!}
                        originAirportCode={airport?.code}
                      />}
                      <BedbankRoomOptions
                        packageViews={packageViews}
                        fetchingRates={fetchingRates}
                        offer={offer}
                        onViewPackageOptions={onViewPackageOptions}
                      />
                    </VerticalSpacer>
                  </LayoutContainer>
                  {!canShowYmalFooter &&
                    <BedbankOfferPageStickyFooter
                      offer={offer}
                      rooms={rooms}
                      duration={duration}
                      checkInDate={checkInDate}
                      checkOutDate={checkOutDate}
                      onScrollTo={onViewPackageOptions}
                      flightPrice={flightPrice}
                      flightsEnabled={flightsEnabled}
                    />}
                </AnalyticsComponentContext.Provider>
              </VerticalSpacer>
            </VerticalSpacer>
          </CSSBreakpoint>
        </SoftOfferPageWalledGarden>
      </PackageOptionsPane>
    </UserAnalyticsActionContext.Provider>
  )
}

type MappedProps = Pick<Props,
  'activeFilter' |
  'airport' |
  'checkInDate' |
  'checkOutDate' |
  'duration' |
  'rooms' |
  'offer' |
  'isCalendarEnabled' |
  'isNightBarEnabled' |
  'isFlightsSelected'
> & { open: boolean }

export default withOfferPageAndReduxState((offerState, state): MappedProps => ({
  airport: offerState.airport,
  checkInDate: offerState.checkInDate,
  checkOutDate: offerState.checkOutDate,
  offer: offerState.bedbankOffer!,
  activeFilter: offerState.activeFilter,
  rooms: offerState.rooms,
  duration: offerState.duration,
  isCalendarEnabled: isFlashCalendarEnabled(offerState, state),
  isNightBarEnabled: isAnyCalendarExist(offerState, state),
  isFlightsSelected: offerState.flights,
  open: !!offerState.activeFilter,
}), BedbankOfferContent, preventRenderWithOpenFilter)
